/**
 * REF-2 — the Refund Log's filter dropdown options, from GET /sales/live/v2/refund-log-filters
 * (`type=type|reason|staff|channel`). NOT YET DEPLOYED (per product, 2026-09-23) — wired now so
 * the dropdowns work the moment it ships. Until then every menu shows its own failure + retry.
 *
 * Lazy per-menu: nothing fetches until a dropdown is actually opened (`ensureLoaded`), and each
 * of the four kinds is cached independently so reopening a menu doesn't refetch it. The whole
 * cache resets when the period or sales-source toggle changes, since 'reason' / 'staff' /
 * 'channel' are scoped to "what actually occurred in the date range" — only 'type' is a fixed
 * list, but it goes through the same cache for a uniform loading/retry story across all four.
 */
import { useCallback, useEffect, useState } from 'react';
import { useBranch } from '../../../context/BranchContext';
import { useAuth } from '../../../context/AuthContext';
import { getRefundLogFiltersApi } from '../../../redux/reports/clickHouse/clickHouseApi';
import type { OrderSource } from '../../../redux/reports/clickHouse/clickHouseApi';
import type { RefundLogFilterKind } from '../../../interface/clickHouseInterface';
import type { RefundSalesSource } from './refundModel';

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const toOrderSource = (source: RefundSalesSource): OrderSource | undefined =>
    source === 'direct' ? 'direct' : source === 'thirdparty' ? 'thirdparty' : undefined;

interface FilterEntryState {
    values: string[];
    loading: boolean;
    failure: boolean;
    loaded: boolean;
}

const EMPTY_ENTRY: FilterEntryState = { values: [], loading: false, failure: false, loaded: false };
const EMPTY_STATE: Record<RefundLogFilterKind, FilterEntryState> = {
    type: EMPTY_ENTRY, reason: EMPTY_ENTRY, staff: EMPTY_ENTRY, channel: EMPTY_ENTRY,
};

export interface UseRefundLogFilterOptionsArgs {
    startDate?: string;
    endDate?: string;
    source: RefundSalesSource;
}

export interface UseRefundLogFilterOptionsResult {
    state: Record<RefundLogFilterKind, FilterEntryState>;
    /** Call when a dropdown opens — fetches once, no-ops if already loaded or in flight. */
    ensureLoaded: (kind: RefundLogFilterKind) => void;
    /** Force a refetch, e.g. from a "Retry" link inside the dropdown. */
    retry: (kind: RefundLogFilterKind) => void;
}

export function useRefundLogFilterOptions({ startDate, endDate, source }: UseRefundLogFilterOptionsArgs): UseRefundLogFilterOptionsResult {
    const { selectedBranch: branch } = useBranch();
    const { auth } = useAuth();
    const locationId = branch?.id ?? branch?.locationId ?? '';
    const token = auth ? findToken(auth) : null;
    const orderSource = toOrderSource(source);

    const [state, setState] = useState<Record<RefundLogFilterKind, FilterEntryState>>(EMPTY_STATE);

    // A period or channel change invalidates whatever was already fetched, so the next time a
    // menu opens it re-fetches instead of showing options from a different date range.
    useEffect(() => { setState(EMPTY_STATE); }, [locationId, startDate, endDate, orderSource]);

    const fetchKind = useCallback((kind: RefundLogFilterKind) => {
        if (!locationId || !token) return;
        setState((prev) => ({ ...prev, [kind]: { ...prev[kind], loading: true, failure: false } }));
        getRefundLogFiltersApi({ locationId, type: kind, startDate, endDate, orderSource, token })
            .then((res: any) => {
                const values = Array.isArray(res?.values) ? res.values : [];
                setState((prev) => ({ ...prev, [kind]: { values, loading: false, failure: false, loaded: true } }));
            })
            .catch(() => {
                setState((prev) => ({ ...prev, [kind]: { values: [], loading: false, failure: true, loaded: false } }));
            });
    }, [locationId, token, startDate, endDate, orderSource]);

    const ensureLoaded = useCallback((kind: RefundLogFilterKind) => {
        if (state[kind].loading || state[kind].loaded) return;
        fetchKind(kind);
    }, [state, fetchKind]);

    return { state, ensureLoaded, retry: fetchKind };
}
